import { useState, useCallback, useEffect } from 'react';
import automatedGenerationService from '../services/automatedGenerationService';

/**
 * Custom hook for managing automated generation
 * 
 * @param {string} projectId - The project ID
 * @param {Function} showNotification - Function to show notifications
 * @returns {Object} Automated generation hook results
 */
const useAutomatedGeneration = (projectId, showNotification) => {
  const [generationStatus, setGenerationStatus] = useState({
    status: 'idle', // 'idle', 'generating', 'paused', 'completed', 'error'
    currentTask: null,
    completed: 0,
    total: 0,
    error: null,
    completedTasks: [],
    failedTasks: []
  });

  const [isLoading, setIsLoading] = useState(false);
  const [progressLog, setProgressLog] = useState([]);

  // Load initial status
  useEffect(() => {
    if (projectId) {
      loadGenerationStatus();
    }
  }, [projectId]);

  // Load current generation status
  const loadGenerationStatus = useCallback(async () => {
    if (!projectId) return;

    try {
      const response = await automatedGenerationService.getGenerationStatus(projectId);
      const statusData = response?.data || response || {};
      
      setGenerationStatus(prev => ({
        ...prev,
        status: statusData?.status || 'idle',
        currentTask: statusData?.current_task || null,
        completed: statusData?.completed || 0,
        total: statusData?.total || 0,
        error: statusData?.error || null,
        completedTasks: statusData?.completed_tasks || [],
        failedTasks: statusData?.failed_tasks || [],
        summary: statusData?.summary || null
      }));
    } catch (error) {
      console.error('Error loading generation status:', error);
      // Ensure state is still properly set even if API call fails
      setGenerationStatus(prev => ({
        ...prev,
        status: 'idle',
        error: null
      }));
    }
  }, [projectId]);

  // Add entry to progress log
  const addToProgressLog = useCallback((entry) => {
    const timestamp = new Date().toISOString();
    setProgressLog(prev => [...prev, { ...entry, timestamp }]);
  }, []);

  // Start automated generation
  const startGeneration = useCallback(async () => {
    if (!projectId) {
      showNotification({
        message: 'Project ID is required to start generation',
        type: 'error'
      });
      return;
    }

    setIsLoading(true);
    setProgressLog([]);
    
    try {
      // Show initial notification
      showNotification({
        message: 'Starting automated generation of all project tasks',
        type: 'info'
      });

      addToProgressLog({
        type: 'info',
        message: 'Initializing automated generation...',
        task: null
      });

      // Start generation with streaming
      await automatedGenerationService.startGenerationWithStreaming(
        projectId,
        // Progress callback
        (progressData) => {
          console.log('Generation progress:', progressData);
          
          const safeProgressData = progressData || {};
          
          setGenerationStatus(prev => ({
            ...prev,
            status: safeProgressData.status || prev.status,
            currentTask: safeProgressData.current_task || safeProgressData.currentTask,
            completed: safeProgressData.completed || prev.completed,
            total: safeProgressData.total_tasks || safeProgressData.total || prev.total,
            summary: safeProgressData.summary || prev.summary
          }));

          // Add to progress log - only add once per task to avoid duplicates
          if (safeProgressData.status === 'generating' && safeProgressData.current_task) {
            // Check if we already logged this task to avoid duplicates
            const lastEntry = progressLog[progressLog.length - 1];
            if (!lastEntry || lastEntry.task !== safeProgressData.current_task || lastEntry.type !== 'generating') {
              addToProgressLog({
                type: 'generating',
                message: `Generating ${safeProgressData.current_task.replace('-', ' ')}...`,
                task: safeProgressData.current_task
              });
            }
          } else if (safeProgressData.status === 'streaming') {
            // Don't log every chunk, just update status
            setGenerationStatus(prev => ({
              ...prev,
              status: 'generating'
            }));
          }
        },
        // Complete callback
        (result) => {
          console.log('Generation completed:', result);
          
          const safeResult = result || {};
          
          setGenerationStatus(prev => ({
            ...prev,
            status: 'completed',
            currentTask: null,
            completed: safeResult.completed_tasks?.length || prev.completed,
            completedTasks: safeResult.completed_tasks || [],
            failedTasks: safeResult.failed_tasks || [],
            summary: safeResult.summary || `Generation completed! ${safeResult.completed_tasks?.length || 0} tasks generated successfully.`
          }));

          addToProgressLog({
            type: 'success',
            message: safeResult.summary || `Generation completed! ${safeResult.completed_tasks?.length || 0} tasks generated successfully.`,
            task: null
          });

          showNotification({
            message: safeResult.summary || `Automated generation completed! Generated ${safeResult.completed_tasks?.length || 0} tasks.`,
            type: 'success'
          });
        },
        // Error callback
        (error) => {
          console.error('Generation error:', error);
          
          setGenerationStatus(prev => ({
            ...prev,
            status: 'error',
            error: error.message
          }));

          addToProgressLog({
            type: 'error',
            message: `Generation failed: ${error.message}`,
            task: null
          });

          showNotification({
            message: `Generation failed: ${error.message}`,
            type: 'error'
          });
        }
      );

    } catch (error) {
      console.error('Error starting generation:', error);
      
      setGenerationStatus(prev => ({
        ...prev,
        status: 'error',
        error: error.message
      }));

      addToProgressLog({
        type: 'error',
        message: `Failed to start generation: ${error.message}`,
        task: null
      });

      showNotification({
        message: `Failed to start generation: ${error.message}`,
        type: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  }, [projectId, showNotification, addToProgressLog]);

  // Pause generation
  const pauseGeneration = useCallback(async () => {
    if (!projectId) return;

    try {
      await automatedGenerationService.pauseGeneration(projectId);
      
      setGenerationStatus(prev => ({
        ...prev,
        status: 'paused'
      }));

      addToProgressLog({
        type: 'warning',
        message: 'Generation paused by user',
        task: generationStatus.currentTask
      });

      showNotification({
        message: 'Generation paused',
        type: 'warning'
      });
    } catch (error) {
      console.error('Error pausing generation:', error);
      showNotification({
        message: `Failed to pause generation: ${error.message}`,
        type: 'error'
      });
    }
  }, [projectId, generationStatus.currentTask, showNotification, addToProgressLog]);

  // Resume generation
  const resumeGeneration = useCallback(async () => {
    if (!projectId) return;

    try {
      await automatedGenerationService.resumeGeneration(projectId);
      
      setGenerationStatus(prev => ({
        ...prev,
        status: 'generating'
      }));

      addToProgressLog({
        type: 'info',
        message: 'Generation resumed',
        task: generationStatus.currentTask
      });

      showNotification({
        message: 'Generation resumed',
        type: 'info'
      });
    } catch (error) {
      console.error('Error resuming generation:', error);
      showNotification({
        message: `Failed to resume generation: ${error.message}`,
        type: 'error'
      });
    }
  }, [projectId, generationStatus.currentTask, showNotification, addToProgressLog]);

  // Reset generation state
  const resetGeneration = useCallback(async () => {
    if (!projectId) return;

    try {
      await automatedGenerationService.resetGeneration(projectId);
      
      setGenerationStatus({
        status: 'idle',
        currentTask: null,
        completed: 0,
        total: 0,
        error: null,
        completedTasks: [],
        failedTasks: []
      });

      setProgressLog([]);

      addToProgressLog({
        type: 'info',
        message: 'Generation state reset',
        task: null
      });

      showNotification({
        message: 'Generation state reset',
        type: 'info'
      });
    } catch (error) {
      console.error('Error resetting generation:', error);
      showNotification({
        message: `Failed to reset generation: ${error.message}`,
        type: 'error'
      });
    }
  }, [projectId, showNotification, addToProgressLog]);

  // Restart generation from a specific task or from the beginning
  const restartGeneration = useCallback(async (fromTask = null) => {
    if (!projectId) {
      showNotification({
        message: 'Project ID is required to restart generation',
        type: 'error'
      });
      return;
    }

    setIsLoading(true);
    setProgressLog([]);
    
    try {
      // Show initial notification
      const restartMessage = fromTask ? 
        `Restarting automated generation from ${fromTask.replace('-', ' ')}` :
        'Restarting automated generation from the beginning';
      
      showNotification({
        message: restartMessage,
        type: 'info'
      });

      addToProgressLog({
        type: 'info',
        message: `Restarting generation${fromTask ? ` from ${fromTask}` : ' from beginning'}...`,
        task: fromTask
      });

      // Start restart with streaming
      await automatedGenerationService.restartGenerationWithStreaming(
        projectId,
        fromTask,
        // Progress callback
        (progressData) => {
          console.log('Restart progress:', progressData);
          
          const safeProgressData = progressData || {};
          
          setGenerationStatus(prev => ({
            ...prev,
            status: safeProgressData.status || prev.status,
            currentTask: safeProgressData.current_task || safeProgressData.currentTask,
            completed: safeProgressData.completed || prev.completed,
            total: safeProgressData.total_tasks || safeProgressData.total || prev.total,
            summary: safeProgressData.summary || prev.summary
          }));

          // Add to progress log
          if (safeProgressData.status === 'generating' && safeProgressData.current_task) {
            addToProgressLog({
              type: 'generating',
              message: `Generating ${safeProgressData.current_task.replace('-', ' ')}...`,
              task: safeProgressData.current_task
            });
          } else if (safeProgressData.status === 'restarted') {
            addToProgressLog({
              type: 'info',
              message: safeProgressData.current_task || 'Generation restarted',
              task: null
            });
          }
        },
        // Complete callback
        (result) => {
          console.log('Restart completed:', result);
          
          const safeResult = result || {};
          
          setGenerationStatus(prev => ({
            ...prev,
            status: 'completed',
            currentTask: null,
            completed: safeResult.completed_tasks?.length || prev.completed,
            completedTasks: safeResult.completed_tasks || [],
            failedTasks: safeResult.failed_tasks || [],
            summary: safeResult.summary || `Restart completed! ${safeResult.completed_tasks?.length || 0} tasks generated successfully.`
          }));

          addToProgressLog({
            type: 'success',
            message: safeResult.summary || `Restart completed! ${safeResult.completed_tasks?.length || 0} tasks generated successfully.`,
            task: null
          });

          showNotification({
            message: safeResult.summary || `Restart completed! Generated ${safeResult.completed_tasks?.length || 0} tasks.`,
            type: 'success'
          });
        },
        // Error callback
        (error) => {
          console.error('Restart error:', error);
          
          setGenerationStatus(prev => ({
            ...prev,
            status: 'error',
            error: error.message
          }));

          addToProgressLog({
            type: 'error',
            message: `Restart failed: ${error.message}`,
            task: null
          });

          showNotification({
            message: `Restart failed: ${error.message}`,
            type: 'error'
          });
        }
      );

    } catch (error) {
      console.error('Error restarting generation:', error);
      
      setGenerationStatus(prev => ({
        ...prev,
        status: 'error',
        error: error.message
      }));

      addToProgressLog({
        type: 'error',
        message: `Failed to restart generation: ${error.message}`,
        task: null
      });

      showNotification({
        message: `Failed to restart generation: ${error.message}`,
        type: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  }, [projectId, showNotification, addToProgressLog]);

  // Calculate progress percentage
  const progressPercentage = generationStatus.total > 0 
    ? Math.round((generationStatus.completed / generationStatus.total) * 100)
    : 0;

  // Check if generation can be started
  const canStartGeneration = generationStatus.status === 'idle' && !isLoading;

  // Check if generation is active
  const isGenerating = generationStatus.status === 'generating';
  const isPaused = generationStatus.status === 'paused';
  const isCompleted = generationStatus.status === 'completed';
  const hasError = generationStatus.status === 'error';

  return {
    // State
    generationStatus,
    isLoading,
    progressLog,
    progressPercentage,
    
    // Status flags
    canStartGeneration,
    isGenerating,
    isPaused,
    isCompleted,
    hasError,
    
    // Actions
    startGeneration,
    pauseGeneration,
    resumeGeneration,
    resetGeneration,
    restartGeneration,
    loadGenerationStatus,
    
    // Utilities
    addToProgressLog
  };
};

export default useAutomatedGeneration;